// SubjectsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  Text,
  Heading,
  Button,
  HStack,
  Spinner,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import BreadcrumbsNav from "./BreadcrumbsNav";
import { BASE_API_URL } from "./config";
import SubjectCard from "./SubjectCard";
import PastPapersCard from "./PastPapersCard";
import TopicalCard from "./TopicalCard";
import usePaperActions from "./hooks/usePaperActions";
import { a } from "./appColors";
import { solidButton } from "./buttonStyles";

const useQuery = () => new URLSearchParams(useLocation().search);

const pill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  px: "16px",
  py: "8px",
  borderRadius: "999px",
  bg: a.pillBg,
  border: "1px solid",
  borderColor: a.border,
  color: a.pillText,
  fontSize: "20px",
  fontWeight: "500",
};

const formatIntake = (v) => {
  if (!v) return "";
  const s = String(v).toUpperCase();
  if (s.includes("MAY") && s.includes("JUNE")) return "May/June";
  if (s.includes("OCT") || s.includes("NOV")) return "October/November";
  if (s.includes("MARCH")) return "March";
  if (s === "MAY_JUNE") return "May/June";
  if (s === "OCTOBER_NOVEMBER") return "October/November";
  return v;
};

const SubjectsPage = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const boardGuid = q.get("boardGuid");
  const qualificationGuid = q.get("qualificationGuid");
  const boardName = q.get("boardName") || "";
  const qualificationCode = q.get("qualificationCode") || "";
  const subjectGuid = q.get("subjectGuid");
  const subjectName = q.get("subjectName") || "";
  const urlTab = q.get("resourceTab");
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [resourceTab, setResourceTab] = useState(urlTab || "past");
  const [pastPapers, setPastPapers] = useState([]);
  const [topicalPapers, setTopicalPapers] = useState([]);
  const [error, setError] = useState("");
  const [showInProgressModal, setShowInProgressModal] = useState(false);

  const { prefetchMarkSchemes, downloadQP, downloadMS, solvePaper, solveTopicalPaper } = usePaperActions();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BASE_API_URL}/api/v1/subjects/by-exam-qualification-guid/${qualificationGuid}`)
      .then((r) => {
        setSubjects(r.data || []);
        setError("");
      })
      .catch(() => setError("Failed to load subjects"))
      .finally(() => setLoading(false));
  }, [qualificationGuid]);

  useEffect(() => {
    if (!subjectGuid) return;
    setLoading(true);
    axios
      .get(`${BASE_API_URL}/api/v1/past-papers/by-subject-guid/${subjectGuid}`)
      .then((r) => setPastPapers(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subjectGuid]);

  useEffect(() => {
    if (!subjectGuid) return;
    setLoading(true);
    axios
      .get(`${BASE_API_URL}/api/v1/subject-general-topics/with-topical-papers/by-subject-guid/${subjectGuid}`)
      .then((r) => {
        setTopicalPapers(r.data || []);
        setError("");
      })
      .catch(() => setError("Failed to load topical papers"))
      .finally(() => setLoading(false));
  }, [subjectGuid]);

  useEffect(() => {
    if (pastPapers.length) prefetchMarkSchemes(pastPapers);
  }, [pastPapers, prefetchMarkSchemes]);

  const crumbs = useMemo(() => {
    const base = [
      { label: boardName, onClick: () => navigate("/home") },
      {
        label: qualificationCode,
        onClick: () =>
          navigate(
            `/subjects?boardGuid=${boardGuid}&boardName=${encodeURIComponent(
              boardName
            )}&qualificationGuid=${qualificationGuid}&qualificationCode=${encodeURIComponent(
              qualificationCode
            )}`
          ),
      },
    ];
    if (subjectGuid) base.push({ label: subjectName, onClick: () => {} });
    if (subjectGuid && resourceTab)
      base.push({ label: resourceTab === "past" ? "Past Papers" : "Topical", onClick: () => {} });
    return base;
  }, [boardGuid, qualificationGuid, subjectGuid, subjectName, resourceTab, boardName, qualificationCode, navigate]);

  const pastGroups = useMemo(() => {
    const m = {};
    for (const p of pastPapers) {
      const intake = `${formatIntake(p.exam_series_month)} ${p.exam_series_year}`.trim();
      const label = p.name || `Paper ${p.code || ""}`.trim();
      if (!m[intake]) m[intake] = [];
      m[intake].push({
        id: p.guid,
        label,
        qpUrl: p.question_paper_file_url || p.file_url,
      });
    }
    return m;
  }, [pastPapers]);

  const topicalChapters = useMemo(() => {
    const groups = {};
    for (const t of topicalPapers || []) {
      const chapterTitle = (t.chapter || t.name || t.title || "Topic").trim();
      const papers = Array.isArray(t.topical_papers) ? t.topical_papers : [];
      if (!groups[chapterTitle]) groups[chapterTitle] = [];
      papers.forEach((tp, idx) => {
        groups[chapterTitle].push({
          id: tp.guid || `${chapterTitle}-${idx}`,
          label: tp.name || tp.title || tp.code || `Topical Paper ${idx + 1}`,
          code: tp.code,
          marks: tp.total_marks ?? tp.marks,
        });
      });
    }
    return groups;
  }, [topicalPapers]);

  return (
    <VStack align="start" spacing="20px" w="100%">
      <BreadcrumbsNav items={crumbs} />
      <HStack mt="24px">
        <Box sx={pill}>
          <Text>{boardName}</Text>
          <Box w="9px" h="9px" borderRadius="50%" bg={a.accent} />
          <Text>
            {qualificationCode}
            {subjectGuid ? ` • ${subjectName}` : ""}
          </Text>
        </Box>
      </HStack>

      {!subjectGuid && (
        <>
          <Heading color={a.text} fontSize="20px" fontWeight="500" mt="34px">
            Subjects Available
          </Heading>
          {loading && <Spinner size="lg" color="#FF4716" />}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="20px" spacingY="16px" w="100%">
            {subjects.map((s) => {
              const baseQuery =
                `/subjects?boardGuid=${boardGuid}` +
                `&boardName=${encodeURIComponent(boardName)}` +
                `&qualificationGuid=${qualificationGuid}` +
                `&qualificationCode=${encodeURIComponent(qualificationCode)}` +
                `&subjectGuid=${s.guid}` +
                `&subjectName=${encodeURIComponent(s.name)}`;
              return (
                <SubjectCard
                  key={s.guid}
                  subjectName={`${s.name} (${s.code})`}
                  onAddSubject={() => {}}
                  onOpenPastPapers={() => navigate(`${baseQuery}&resourceTab=past`)}
                  onOpenTopical={() => navigate(`${baseQuery}&resourceTab=topical`)}
                />
              );
            })}
          </SimpleGrid>
        </>
      )}

      {!!subjectGuid && (
        <>
          <HStack spacing="12px" mt="8px">
            <Button
              onClick={() => setResourceTab("past")}
              variant="ghost"
              px="14px"
              py="8px"
              borderRadius="28px"
              border="1px solid" borderColor={a.border}
              bg={resourceTab === "past" ? a.tabActiveBg : a.surface}
              color={resourceTab === "past" ? "#000000" : a.text}
              fontSize="14px"
              fontWeight="400"
              lineHeight="normal"
              h="auto"
            >
              Past Papers
            </Button>
            <Button
              onClick={() => setResourceTab("topical")}
              variant="ghost"
              px="14px"
              py="8px"
              borderRadius="28px"
              border="1px solid" borderColor={a.border}
              bg={resourceTab === "topical" ? a.tabActiveBg : a.surface}
              color={resourceTab === "topical" ? "#000000" : a.text}
              fontSize="14px"
              fontWeight="400"
              lineHeight="normal"
              h="auto"
            >
              Topical
            </Button>
          </HStack>

          {resourceTab === "past" && (
            <>
              <Heading color={a.text} fontSize="20px" fontWeight="500" mt="16px">
                Past Papers
              </Heading>
              {loading && <Spinner size="lg" color={a.accent} />}
              <VStack align="start" spacing="16px" mt="8px" w={{ base: "100%", md: "661px" }}>
                {Object.entries(pastGroups).map(([intake, items]) => (
                  <PastPapersCard
                    key={intake}
                    title={intake}
                    items={items}
                    onSolveRow={(p) =>
                      solvePaper(p.id, {
                        setLoading,
                        setShowInProgressModal,
                        setError,
                      })
                    }
                    onDownloadQPRow={(p) => downloadQP(p.qpUrl)}
                    onDownloadMSRow={(p) => downloadMS(p.id)}
                  />
                ))}
              </VStack>
            </>
          )}

          {resourceTab === "topical" && (
            <>
              <Heading color={a.text} fontSize="20px" fontWeight="500" mt="16px">
                Topical
              </Heading>
              {loading && <Spinner size="lg" color={a.accent} />}
              {error && (
                <Text color={a.textSub} fontSize="14px" fontWeight="300">
                  {error}
                </Text>
              )}
              <VStack align="start" spacing="16px" mt="8px" w={{ base: "100%", md: "661px" }}>
                {Object.entries(topicalChapters).map(([chapter, items]) => (
                  <TopicalCard
                    key={chapter}
                    title={chapter}
                    items={items}
                    onSolveRow={(row) =>
                      solveTopicalPaper(row.id, {
                        setLoading,
                        setShowInProgressModal,
                        setError,
                      })
                    }
                  />
                ))}
              </VStack>
            </>
          )}
        </>
      )}

      <Modal isOpen={showInProgressModal} onClose={() => setShowInProgressModal(false)}>
        <ModalOverlay />
        <ModalContent display="inline-flex" gap="26px" borderRadius="14px" background={a.surface}>
          <ModalHeader color={a.text} fontSize="20px" fontWeight="500">
            Attempt in Progress
          </ModalHeader>
          <ModalCloseButton color={a.text} />
          <ModalBody>
            <Text fontSize="14px" fontWeight="400" color={a.text}>
              You have already started solving this paper. You can access it from "My Papers" or
              continue from where you stopped.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              {...solidButton}
              borderRadius="14px"
              padding="8px 8px"
              fontSize="14px"
              fontWeight="400"
              onClick={() => {
                setShowInProgressModal(false);
                navigate("/my-papers");
              }}
            >
              Access paper
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default SubjectsPage; 



